import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type ReprocessingInvoiceProcessStatusParams,
  type ReprocessingInvoiceProcessStatusResponse,
} from './types';
import { zodReprocessingInvoiceProcessStatusEndpointSchema } from './validations';

/**
 * Fetches the status of an invoice process based on the process ID.
 * @param params - The parameters for fetching the invoice status.
 * @returns The invoice status data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_process_status_invoice_process__process_id__status_get
 */
export const fetchReprocessingInvoiceProcessStatus = async (
  params: ReprocessingInvoiceProcessStatusParams,
): Promise<ReprocessingInvoiceProcessStatusResponse['status']> => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.INVOICE_REPROCESSING.GET_STATUS(params.processId), {
        validate: {
          pathSegments: zodReprocessingInvoiceProcessStatusEndpointSchema,
        },
      })
      .json<ReprocessingInvoiceProcessStatusResponse>();

    return response.status;
  } catch (error) {
    log.error({
      message: 'Error fetching reprocessing invoice process status',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      errorMessage = i18n.t(
        ($) =>
          $.invoiceReprocessingQueries.fetchReprocessingInvoiceProcessStatus
            .error.generic,
        { ns: 'data' },
      );
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
